import React, { useMemo } from 'react';

import type AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';
import { useIntl } from 'react-intl';
import type { EntryPointProps } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import type { EMBEDDED_CONFLUENCE_MODE } from '@atlassian/embedded-confluence-common/page-common/EmbeddedConfluenceMode';
import { EmbeddedConfluence } from '@atlassian/embedded-confluence/embedded-confluence';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { PreviewPanel } from '@atlassian/preview-panel/preview-panel';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { PreviewPanelActionGroup } from '@atlassian/preview-panel/preview-panel-action-group';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { PreviewPanelBody } from '@atlassian/preview-panel/preview-panel-body';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { PreviewPanelExpand } from '@atlassian/preview-panel/preview-panel-expand';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { PreviewPanelHeader } from '@atlassian/preview-panel/preview-panel-header';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { PreviewPanelTitle } from '@atlassian/preview-panel/preview-panel-title';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { usePreviewPanelsNavigation } from '@atlassian/preview-panels-api/preview-panel-navigation';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api/use-preview-panel-actions';

import { type PanelDataMap, type PARENT_PRODUCTS } from '../../common/constants';
import { messages } from './messages';
import { constructUrl, initializeAnalyticsClient, onEditMode } from './utils';
export type ConfluenceContentType = 'page' | 'blogpost' | 'whiteboard';

export interface ConfluencePagePreviewPanelProps {
	cloudId?: string;
	contentType?: ConfluenceContentType;
	panelData?: PanelDataMap;
	parentProduct: (typeof PARENT_PRODUCTS)[keyof typeof PARENT_PRODUCTS];
	url: string;
}

export type ConfluencePagePreviewPanelEntrypointProps = EntryPointProps<
	Record<string, never>,
	Record<string, never>,
	ConfluencePagePreviewPanelProps,
	Record<string, never>
>;

const styles = cssMap({
	body: {
		flexGrow: 1,
		overflowY: 'hidden',
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingLeft: token('space.300'),
	},
});

const contentTypeTitleMessages = {
	page: messages.pageTitle,
	blogpost: messages.blogPostTitle,
	whiteboard: messages.whiteboardTitle,
} as const;

export const ConfluencePagePreviewPanel = ({
	url,
	parentProduct,
	cloudId,
	contentType = 'page',
	panelData,
}: ConfluencePagePreviewPanelProps): React.JSX.Element => {
	const { close, setPanelWidth } = usePreviewPanelsActions();
	const { canNavigateBack, navigateBack } = usePreviewPanelsNavigation();
	const [analyticsClient, setAnalyticsClient] = React.useState<AnalyticsWebClient>();
	const { formatMessage } = useIntl();
	const themeState = useThemeObserver();
	const { actions } = panelData || {};

	React.useEffect(() => {
		setAnalyticsClient(initializeAnalyticsClient(cloudId));
	}, [cloudId, setAnalyticsClient]);

	const onModeChange = (mode: EMBEDDED_CONFLUENCE_MODE) => onEditMode(mode, setPanelWidth);

	const onClose = () => {
		actions?.onClose?.();
		close();
	};

	const formattedUrl = useMemo(
		() => constructUrl(themeState, url, panelData),
		[panelData, url, themeState],
	);
	const embeddedConfluenceSource = (() => {
		try {
			const urlObj = new URL(formattedUrl);
			return urlObj.searchParams.get('embeddedConfluenceSource');
		} catch {
			return null;
		}
	})();
	const runtimeProps = {
		url: formattedUrl,
		showFooter: false,
		hasFooterLogo: false,
		onModeChange: onModeChange,
		onPublish: actions?.onPublish,
		allowedFeatures: panelData?.allowedFeatures,
		parentProduct: parentProduct,
		embeddedConfluenceSource: embeddedConfluenceSource || 'confluence-page-preview-panel',
		themeState: themeState,
	};

	return (
		<PreviewPanel testId="confluence-preview-panel">
			<PreviewPanelHeader
				analyticsClient={analyticsClient}
				parentProduct={parentProduct}
				panelType={'confluence-page'}
				onClose={onClose}
				onNavigateBack={
					canNavigateBack && fg('jivtech-1920-back-navigation-gpp') ? navigateBack : undefined
				}
				onOpenInNewTab={actions?.onOpenInNewTab}
				url={formattedUrl}
			>
				<PreviewPanelTitle
					title={formatMessage(contentTypeTitleMessages[contentType])}
					icon={
						contentType === 'blogpost' ? (
							<QuotationMarkIcon label="" size="medium" />
						) : (
							<PageIcon label="" size="medium" />
						)
					}
				/>
				{!!actions && (
					<PreviewPanelActionGroup>
						{actions.onExpand && <PreviewPanelExpand onClick={actions.onExpand} />}
					</PreviewPanelActionGroup>
				)}
			</PreviewPanelHeader>
			<PreviewPanelBody
				analyticsClient={analyticsClient}
				parentProduct={parentProduct}
				panelType={'confluence-page'}
				styles={styles.body}
			>
				<EmbeddedConfluence {...runtimeProps} />
			</PreviewPanelBody>
		</PreviewPanel>
	);
};

const ConfluencePagePreviewPanelEntryPointComponent = ({
	props: { url, parentProduct, cloudId, contentType, panelData },
}: ConfluencePagePreviewPanelEntrypointProps): React.JSX.Element => (
	<ConfluencePagePreviewPanel
		url={url}
		parentProduct={parentProduct}
		cloudId={cloudId}
		contentType={contentType}
		panelData={panelData}
	/>
);

export default ConfluencePagePreviewPanelEntryPointComponent;
